import React, { useEffect, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import Link from 'next/link';

import axiosApiInstance from '@/api/shared/axios-instance';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import InProgressComponent from '@/components/shared/custom/in-progress';
import DateTimeWrapper from '@/components/shared/datetime-wrapper';
import { Rutas } from '@/constants/rutas';
import { getTranslatedTeamName, obtenerEquipo } from '@/data/equipos';
import { translate } from '@/data/fixture-transalations';
import { getTimeShortWithYear } from '@/helpers/datetime-helper';
import { getDisplayLiveIndicator, getFixtureScore, getFixtureStatus } from '@/helpers/fixtures-helper';
import { getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import URL_FACTORY from '@/helpers/url-helper';

import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';

const FixtureBox = ({ fixtureId, fixture, events, mb: _mb = 0 }) => {
    const [fixtureBox, setFixtureBox] = useState(fixture);
    const theme = useTheme();

    const getFixtureBox = () => {
        const url = URL_FACTORY.GetFixtureBox(fixtureId);

        axiosApiInstance.get(url).then((response) => {
            setFixtureBox(response.data);
        });
    };

    useEffect(() => {
        if (fixtureId) {
            getFixtureBox();
        } else if (fixture) {
            setFixtureBox(fixture);
        }
    }, [fixtureId, fixture]);

    if (!fixtureBox) return null;

    const getGoalsContainer = () => {
        if (events?.length > 0 && fixture) {
            let homeGoals = 0;
            let awayGoals = 0;

            return (
                <>
                    {events
                        ?.sort((a, b) => a?.time?.elapsed - b?.time?.elapsed)
                        ?.map((event, index) => {
                            if (event.type === 'Goal' && event.detail !== 'Missed Penalty') {
                                if (fixture?.home?.id === event?.team?.id) {
                                    homeGoals++;
                                } else {
                                    awayGoals++;
                                }
                                return (
                                    <Box
                                        className="game-result-container-goals__line"
                                        key={index}
                                        style={{
                                            justifyContent:
                                                fixture?.away?.id === event?.team?.id
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box className="game-result-container-goals_time">
                                            <Typography fontSize={12}>
                                                {homeGoals}-{awayGoals}
                                            </Typography>
                                        </Box>
                                        <Box className="game-result-container-goal__type">
                                            <Typography fontSize={12}>{event?.player?.name}</Typography>
                                            <Typography fontSize={12}>
                                                {event?.detail === 'Own Goal' ? ' p.p' : ''}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                fontSize: '12px',
                                                position: 'relative',
                                            }}
                                        >
                                            <Typography fontSize={12}>
                                                {event.detail === 'Penalty' ? ' (p.) ' : ''}
                                            </Typography>
                                            <Typography fontSize={12}>
                                                (min {event?.time?.elapsed ?? ''})
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            }
                        })}
                </>
            );
        }
    };

    const getTeamDisplay = (team, isHome = true) => {
        const teamFound = obtenerEquipo(team?.id);
        const teamName = getTranslatedTeamName(team?.id, team?.name);
        const teamScore = getFixtureScore(
            isHome
                ? (fixtureBox?.home?.score ?? fixtureBox?.home?.goals)
                : (fixtureBox?.away?.score ?? fixtureBox?.away?.goals),
            fixtureBox.fixtureStatus
        );

        const teamContent = (
            <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                    minWidth: 0,
                    flex: 1,
                }}
            >
                {isHome ? (
                    <>
                        <Typography
                            fontSize={14}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1,
                                textAlign: 'right',
                            }}
                        >
                            {teamName}
                        </Typography>
                        <CustomImageComponent
                            src={getTeamImageById(team?.id, 60)}
                            width={40}
                            height={40}
                            alt={teamName}
                        />
                        <Box
                            className="game-result-container__teams__result-box"
                            border={`1px solid ${theme.palette.border.main}`}
                            bgcolor={theme.palette.background.darkCard}
                            sx={{
                                minWidth: '40px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '4px 8px',
                            }}
                        >
                            <Typography fontSize={20} textAlign={'center'}>
                                {teamScore}
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            className="game-result-container__teams__result-box"
                            border={`1px solid ${theme.palette.border.main}`}
                            bgcolor={theme.palette.background.darkCard}
                            sx={{
                                minWidth: '40px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '4px 8px',
                            }}
                        >
                            <Typography fontSize={20} textAlign={'center'}>
                                {teamScore}
                            </Typography>
                        </Box>
                        <CustomImageComponent
                            src={getTeamImageById(team?.id, 60)}
                            alt={teamName}
                            width={40}
                            height={40}
                        />
                        <Typography
                            fontSize={14}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1,
                                textAlign: 'left',
                            }}
                        >
                            {teamName}
                        </Typography>
                    </>
                )}
            </Box>
        );

        if (teamFound) {
            return (
                <Link
                    href={`${Rutas.EQUIPO}/${teamFound.slug}`}
                    prefetch={false}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1 }}
                >
                    {teamContent}
                </Link>
            );
        } else {
            return <Box sx={{ display: 'flex', flex: 1 }}>{teamContent}</Box>;
        }
    };

    const isLive = getDisplayLiveIndicator(fixtureBox.fixtureStatus);

    return (
        <CustomSectionWrapper padding={0}>
            <Box className="game-result-container" px={0} mb={0}>
                {/* Primera línea: Liga + Jornada | Live Indicator + Fecha */}
                <Box
                    px={1}
                    py={1}
                    sx={{
                        borderBottom: `1px solid ${theme.palette.border.main}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <CustomImageComponent
                            style={{ marginRight: '5px', position: 'relative', bottom: '2px' }}
                            src={getCompetitionImage(fixtureBox?.league?.id ?? fixtureBox?.leagueId, 23)}
                            alt="competición"
                            width={15}
                            height={15}
                        />
                        <Typography fontSize={14}>
                            {translate(fixtureBox?.league?.round ?? fixtureBox?.round ?? '')}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {isLive && <InProgressComponent hideText size={10} />}
                        <Typography fontSize={14}>
                            <DateTimeWrapper
                                date={fixtureBox?.date}
                                format={getTimeShortWithYear}
                                style={{ fontSize: '14px' }}
                            />
                        </Typography>
                    </Box>
                </Box>

                {/* Segunda línea: Local (nombre, escudo, goles) | Visitante (goles, escudo, nombre) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mt: 1,
                        px: 1,
                        pb: 1,
                    }}
                >
                    {getTeamDisplay(fixtureBox?.home, true)}
                    {getTeamDisplay(fixtureBox?.away, false)}
                </Box>

                {/* Estado del partido debajo del marcador */}
                <Box
                    px={1}
                    pb={1}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography fontSize={14} textAlign="center">
                        {getFixtureStatus(fixtureBox.fixtureStatus)}
                    </Typography>
                </Box>

                {getGoalsContainer()}
            </Box>
        </CustomSectionWrapper>
    );
};

export default FixtureBox;
